import { useTranslation } from "react-i18next";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/shared/Input/Input";
import { useForm } from "react-hook-form";
import useModalStore from "@/store/useModalStore";
import { FunnelType, Upsell } from "@/@types/Upsell.ts";
import Divider from "@/components/shared/Divider/Divider";
import { ColorPickerCustom as ColorPicker } from "@/components/shared/ColorPicker/ColorPicker.tsx";

interface CustomizeButtonsSheetProps {
  upsell: Upsell | null;
  onUpdate: (data: any) => Promise<void>;
  isLoading: boolean;
}

export function CustomizeButtonsSheet({
  upsell,
  onUpdate,
  isLoading,
}: CustomizeButtonsSheetProps) {
  const { t } = useTranslation(["upsells", "common"]);
  const { closeModal } = useModalStore();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: upsell?.name || "",
      funnel_type: upsell?.funnel_type || FunnelType.UpSell,
      page: upsell?.page || "",
      offer_id: upsell?.offer.id || "",
      up_sell_display_text: upsell?.up_sell_display_text || "",
      up_sell_button_color: upsell?.up_sell_button_color || "5CCE5E",
      up_sell_display_text_color:
        upsell?.up_sell_display_text_color || "000000",
      down_sell_display_text: upsell?.down_sell_display_text || "",
      down_sell_button_color: upsell?.down_sell_button_color || "DC2626",
      down_sell_display_text_color:
        upsell?.down_sell_display_text_color || "FFFFFF",
    },
  });
  const defaultValues = watch();

  const handleClose = () => {
    reset();
    closeModal("customize-buttons");
  };

  const onSubmit = async (data: any) => {
    try {
      await onUpdate(data);
      handleClose();
    } catch (error) {
      console.error("Error updating buttons:", error);
    }
  };

  return (
    <section className="sm:overflow-y-hidden flex flex-col justify-between h-full">
      <SheetHeader className="p-6">
        <SheetTitle className="flex gap-2">{t("customize_buttons")}</SheetTitle>
      </SheetHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1">
        <div className="flex-1 px-6 overflow-y-auto space-y-4">
          <div className="space-y-4">
            <Input
              label={t("form.up_sell_display_text")}
              {...register("up_sell_display_text")}
              helperText={errors.up_sell_display_text?.message}
              placeholder={t("form.upsell_accept_placeholder")}
            />

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker
                defaultColor={defaultValues?.up_sell_button_color}
                onValueChange={(value) => {
                  setValue("up_sell_button_color", value);
                }}
                label={t("form.color_bg")}
              />
              <ColorPicker
                defaultColor={defaultValues?.up_sell_display_text_color}
                onValueChange={(value) =>
                  setValue("up_sell_display_text_color", value)
                }
                label={t("form.color_text")}
              />
            </div>
          </div>

          <Divider />

          <div className="space-y-4">
            <Input
              label={t("form.down_sell_display_text")}
              {...register("down_sell_display_text")}
              helperText={errors.down_sell_display_text?.message}
              placeholder={t("form.upsell_decline_placeholder")}
            />

            <div className="grid grid-cols-2 gap-4">
              <ColorPicker
                defaultColor={defaultValues?.down_sell_button_color}
                onValueChange={(value) =>
                  setValue("down_sell_button_color", value)
                }
                label={t("form.color_bg")}
              />
              <ColorPicker
                defaultColor={defaultValues?.down_sell_display_text_color}
                onValueChange={(value) =>
                  setValue("down_sell_display_text_color", value)
                }
                label={t("form.color_text")}
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Button
              type="button"
              className="w-full"
              style={{
                backgroundColor: `${defaultValues.up_sell_button_color}`,
                color: `${defaultValues.up_sell_display_text_color}`,
              }}
            >
              {watch("up_sell_display_text") ||
                t("form.upsell_accept_placeholder")}
            </Button>
            <Button
              type="button"
              className="w-full"
              style={{
                backgroundColor: `${defaultValues.down_sell_button_color}`,
                color: `${defaultValues.down_sell_display_text_color}`,
              }}
            >
              {watch("down_sell_display_text") ||
                t("form.upsell_decline_placeholder")}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t max-sm:pb-12">
          <Button type="button" variant="ghost" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button disabled={isLoading} type="submit">
            {isLoading ? t("common:saving") : t("save_changes")}
          </Button>
        </div>
      </form>
    </section>
  );
}
